import { addDays, startOfCalendarWeek } from '~/calculations/calendar'
import type {
  DomainChangeSource,
  DomainGoalInput,
  DomainGoalRecord,
  WeeklyCommitment,
} from '~/models/fitnessDomain'
import type { WeeklySchedule } from '~/models/trainingSchedule'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import {
  createDomainGoal,
  getDomainGoal,
  transitionDomainGoal,
  transitionDomainGoals,
} from '~/storage/repositories/domainGoalRepository'
import type { CalendarDate } from '~/types/dates'
import { calendarDateFrom } from '~/types/dates'
import type { DomainGoalId, ScheduledSessionId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { getEffectiveWeekStartDay } from './weekBoundaryService'

/**
 * Editing the plan (D-099, as amended at C6.5).
 *
 * **My Plan is the only place a weekly commitment can change**, and this service
 * is the only path it changes through. That is not a statement about screens: a
 * commitment that could also move through `updateDomainGoal` would move with no
 * entry in the goal's history, and the first sign of it would be a review whose
 * plan nobody could account for.
 *
 * Every write here goes through `transitionDomainGoal`, which appends. Nothing
 * in this file writes a record directly.
 */

/**
 * The first week a commitment change applies to.
 *
 * **The start of the next week, never today.** Changing what the current week
 * contains halfway through it moves the target under a week already in progress,
 * which is exactly how a description of a week becomes something to fail — the
 * failure D-099 §C exists to prevent, arriving from the write side rather than
 * from the copy.
 *
 * The week boundary is the user's own `weekStartDay`, so this agrees with the
 * weekly review and the checklist rather than assuming Monday.
 */
export async function nextCommitmentEffectiveDate(
  db: SolaDatabase,
  now: Date,
): Promise<Result<CalendarDate, StorageError>> {
  const weekStartDay = await getEffectiveWeekStartDay(db, now)
  if (!weekStartDay.ok) return weekStartDay

  const today = calendarDateFrom(now)
  const thisWeek = startOfCalendarWeek(today, weekStartDay.value)
  return ok(addDays(thisWeek, DAYS_IN_WEEK))
}

const DAYS_IN_WEEK = 7

/**
 * Replace a goal's weekly commitment, from the start of next week.
 *
 * A commitment identical to the stored one appends nothing and writes nothing —
 * the same rule as a phase-history form saved unchanged. The caller is expected
 * to have shown the user the effective date before calling.
 *
 * **This is the only path a commitment changes through, and a Gemini pre-fill
 * uses it too** (D-093 §6). Not a second write path: C6.5 established the rule
 * when `weeklyCommitment` joined the carried-verbatim set, because two write
 * paths with only one accountable is how a change reaches a record with nothing
 * appended to its history. `source` is a label on the entry, not a different door.
 *
 * **The effective date is unaffected by who proposed the numbers.** A change lands
 * at the start of next week from the user's own `weekStartDay`, and a proposal
 * does not get to move the current week's target under a week already in progress.
 */
export async function setWeeklyCommitment(
  db: SolaDatabase,
  id: DomainGoalId,
  commitment: WeeklyCommitment,
  now: Date,
  reason?: string,
  source?: DomainChangeSource,
): Promise<Result<DomainGoalRecord, StorageError>> {
  const effectiveFrom = await nextCommitmentEffectiveDate(db, now)
  if (!effectiveFrom.ok) return effectiveFrom

  return transitionDomainGoal(db, id, { weeklyCommitment: commitment }, now, {
    effectiveFrom: effectiveFrom.value,
    ...(reason === undefined ? {} : { reason }),
    ...(source === undefined ? {} : { source }),
  })
}

/* ── The recurring schedule ──────────────────────────────────────────────── */

/** One domain's part of a My Plan save. */
export interface ScheduleEdit {
  readonly goalId: DomainGoalId
  readonly schedule: WeeklySchedule
}

/**
 * Save the week the user arranged, across every domain it touches (D-105 §7).
 *
 * **One transaction, and the effective date is the start of next week** — the
 * same boundary a commitment change lands on, for the same reason: a schedule
 * edit on Wednesday must not move anything already on screen. The materialised
 * week is frozen from the schedule in force when it began, so this write is
 * invisible to the running week by construction rather than by a rule anyone has
 * to apply here.
 *
 * The `weeklySchedule` transition supersedes a hand-written `standard`, which is
 * phase A's rule and is not repeated here. `short` and `minimumViable` survive,
 * so D-101 §4's floor is untouched.
 */
export async function saveWeeklySchedules(
  db: SolaDatabase,
  edits: readonly ScheduleEdit[],
  now: Date,
  reason?: string,
): Promise<Result<readonly DomainGoalRecord[], StorageError>> {
  const duplicate = duplicateSessionId(edits)
  if (duplicate !== undefined) {
    return err(
      storageError(
        'invalidRecord',
        'Two sessions in this week share an identifier, so one would absorb the other’s changes.',
      ),
    )
  }

  const refusal = await scheduleRefusal(db, edits)
  if (refusal !== undefined) return err(refusal)

  const effectiveFrom = await nextCommitmentEffectiveDate(db, now)
  if (!effectiveFrom.ok) return effectiveFrom

  return transitionDomainGoals(
    db,
    edits.map((edit) => ({
      id: edit.goalId,
      next: { weeklySchedule: edit.schedule },
      options: {
        effectiveFrom: effectiveFrom.value,
        ...(reason === undefined ? {} : { reason }),
      },
    })),
    now,
  )
}

/** One domain's schedule, through the same path a multi-domain save uses. */
export async function setWeeklySchedule(
  db: SolaDatabase,
  goalId: DomainGoalId,
  schedule: WeeklySchedule,
  now: Date,
  reason?: string,
): Promise<Result<DomainGoalRecord | undefined, StorageError>> {
  const saved = await saveWeeklySchedules(db, [{ goalId, schedule }], now, reason)
  if (!saved.ok) return saved
  return ok(saved.value[0])
}

/**
 * Move one recurring session from one domain to another, atomically.
 *
 * **The session keeps its identifier**, so both histories record a change to a
 * session that still exists rather than a delete on one goal and an unrelated
 * creation on the other (D-105 §7). Ownership transfers whole: removed from the
 * old schedule, added to the new, both goals written together or neither.
 *
 * The weekday and name come with it. Re-arranging the session is a separate
 * edit, and doing both in one call would make the history unable to say which
 * change the user actually made.
 */
export async function transferScheduledSession(
  db: SolaDatabase,
  transfer: {
    readonly sessionId: ScheduledSessionId
    readonly fromGoalId: DomainGoalId
    readonly toGoalId: DomainGoalId
  },
  now: Date,
  reason?: string,
): Promise<Result<readonly DomainGoalRecord[], StorageError>> {
  if (transfer.fromGoalId === transfer.toGoalId) {
    return err(storageError('invalidRecord', 'That session is already in that area.'))
  }

  const from = await getDomainGoal(db, transfer.fromGoalId)
  if (!from.ok) return from
  const to = await getDomainGoal(db, transfer.toGoalId)
  if (!to.ok) return to
  if (from.value === undefined || to.value === undefined) {
    return err(storageError('invalidRecord', 'That area no longer exists.'))
  }

  const moving = (from.value.weeklySchedule ?? []).find(
    (session) => session.id === transfer.sessionId,
  )
  if (moving === undefined) {
    return err(storageError('invalidRecord', 'That session is not in that area’s week.'))
  }

  return saveWeeklySchedules(
    db,
    [
      {
        goalId: transfer.fromGoalId,
        schedule: (from.value.weeklySchedule ?? []).filter(
          (session) => session.id !== transfer.sessionId,
        ),
      },
      { goalId: transfer.toGoalId, schedule: [...(to.value.weeklySchedule ?? []), moving] },
    ],
    now,
    reason,
  )
}

/**
 * One identifier per session across the whole week.
 *
 * `validateWeeklySchedule` already refuses a duplicate inside one domain. It
 * cannot see across them, and a composed editor is exactly where the same id
 * lands on two goals — a half-finished transfer, most obviously. Two goals
 * claiming one session make "which domain is this?" unanswerable.
 */
function duplicateSessionId(edits: readonly ScheduleEdit[]): string | undefined {
  const seen = new Set<string>()
  for (const edit of edits) {
    for (const session of edit.schedule) {
      if (seen.has(session.id)) return session.id
      seen.add(session.id)
    }
  }
  return undefined
}

/**
 * Why this save is refused, if it is.
 *
 * **Adding a session must never activate, resume or re-prioritise a domain**
 * (D-105 §7), so a domain that is not planned against acquires no sessions and
 * the refusal says so — rather than the save quietly turning an opt-in decision
 * the user never made. D-090's opt-in rule is untouched.
 *
 * A **paused** goal may still be edited and keeps its schedule: pausing retains
 * it and only excludes it from future materialisation, so resuming is a
 * resumption rather than a rebuild. Editing one does not resume it.
 */
async function scheduleRefusal(
  db: SolaDatabase,
  edits: readonly ScheduleEdit[],
): Promise<StorageError | undefined> {
  for (const edit of edits) {
    if (edit.schedule.length === 0) continue

    const goal = await getDomainGoal(db, edit.goalId)
    if (!goal.ok) return goal.error
    if (goal.value === undefined) {
      return storageError('invalidRecord', 'That area no longer exists.')
    }
    if (goal.value.intention === 'trackOnly' || goal.value.intention === 'inactive') {
      return storageError(
        'invalidRecord',
        'That area is only being tracked, so it has no sessions to plan. Change what you want from it first.',
      )
    }
  }
  return undefined
}

/**
 * Pause an area without ending it.
 *
 * A paused goal stops taking part in the weekly review, the checklist and Today,
 * because `isParticipating` reads `status`. It keeps its history, its commitment
 * and its start date, so resuming is a resumption rather than a new goal.
 */
export async function pauseDomainGoal(
  db: SolaDatabase,
  id: DomainGoalId,
  now: Date,
  reason?: string,
): Promise<Result<DomainGoalRecord, StorageError>> {
  return transitionDomainGoal(
    db,
    id,
    { status: 'paused' },
    now,
    reason === undefined ? {} : { reason },
  )
}

export async function resumeDomainGoal(
  db: SolaDatabase,
  id: DomainGoalId,
  now: Date,
): Promise<Result<DomainGoalRecord, StorageError>> {
  return transitionDomainGoal(db, id, { status: 'active' }, now)
}

/**
 * End an area, permanently.
 *
 * **There is no transition out of `ended`** — the model refuses it, because
 * reopening would be a new goal with its own start date rather than a revival of
 * this one. The screen must confirm before calling this.
 */
export async function endDomainGoal(
  db: SolaDatabase,
  id: DomainGoalId,
  now: Date,
  reason?: string,
): Promise<Result<DomainGoalRecord, StorageError>> {
  const goal = await getDomainGoal(db, id)
  if (!goal.ok) return goal
  if (goal.value === undefined) {
    return err(storageError('invalidRecord', 'That area no longer exists.'))
  }

  return transitionDomainGoal(db, id, { status: 'ended' }, now, {
    endedOn: calendarDateFrom(now),
    ...(reason === undefined ? {} : { reason }),
  })
}

/**
 * Add an area to a running project.
 *
 * Delegates to `createDomainGoal`, which already refuses a second live goal for
 * a domain — one domain, one goal, enforced where the write happens rather than
 * checked again here.
 */
export async function addDomainGoalToProject(
  db: SolaDatabase,
  input: DomainGoalInput,
  now: Date,
): Promise<Result<DomainGoalRecord, StorageError>> {
  return createDomainGoal(db, input, now)
}
